import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  Row,
  Col,
  Alert,
  Card,
  Spinner,
} from 'react-bootstrap';
import userService from '../services/userService';
import SideMenu from '../components/common/SideMenu';
import AdminMenuButton from '../components/common/AdminMenuButton';
import './Common.scss';
import UserTable from '../components/user/UserTable';
import { User } from '../models/user';

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true); // 初期状態をtrueに変更
  const [isSideMenuOpen, setIsSideMenuOpen] = useState<boolean>(true);

  const handleSideMenuToggle = (isOpen: boolean): void => {
    setIsSideMenuOpen(isOpen);
  };

  // メッセージを自動で消去するヘルパー関数
  const clearMessagesAfterDelay = (): void => {
    setTimeout(() => {
      setError('');
      setSuccess('');
    }, 5000); // 5秒後に自動消去
  };

  // ユーザー一覧を取得する関数
  const fetchUsers = async (showLoader: boolean = false): Promise<void> => {
    if (showLoader) setLoading(true);
    try {
      const response = await userService.getUsers();
      setUsers(response);
      setError('');
    } catch (err) {
      console.error('ユーザー一覧取得エラー:', err);
      setError('ユーザー一覧の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  // ページ初期表示時にユーザー一覧を取得
  useEffect(() => {
    fetchUsers();
  }, []);

  // 手動でリフレッシュするためのボタン用関数
  const handleManualRefresh = (): void => {
    fetchUsers(true);
  };

  const handleUpdateUser = async (updatedUser: User): Promise<void> => {
    try {
      await userService.updateUser(updatedUser);
      // 更新成功時にユーザー一覧を再取得
      await fetchUsers();
      setError(''); // エラーメッセージをクリア
      setSuccess(`ユーザー「${updatedUser.username}」の情報を更新しました。`);
      clearMessagesAfterDelay();
    } catch (error) {
      console.error('ユーザー更新失敗', error);
      setError('ユーザーの更新に失敗しました。');
      setSuccess('');
      clearMessagesAfterDelay();
    }
  };

  const handleDeleteUser = async (userId: number): Promise<void> => {
    const userToDelete = users.find(user => user.id === userId);
    try {
      await userService.deleteUser(userId);
      // 削除成功時にユーザー一覧を再取得
      await fetchUsers();
      setError(''); // エラーメッセージをクリア
      setSuccess(
        `ユーザー「${userToDelete?.username || 'ID:' + userId}」を削除しました。`
      );
      clearMessagesAfterDelay();
    } catch (error) {
      console.error('ユーザー削除失敗', error);
      setError('ユーザーの削除に失敗しました。');
      setSuccess('');
      clearMessagesAfterDelay();
    }
  };

  return (
    <div className="page-layout">
      <AdminMenuButton
        onToggle={handleSideMenuToggle}
        isOpen={isSideMenuOpen}
      />

      <Container fluid>
        <Row className="min-vh-100 g-0">
          {/* サイドバー: 条件付き表示 */}
          {isSideMenuOpen && (
            <Col xs={12} md={3} className="admin-sidebar-col">
              <div className="p-3 position-sticky" style={{ top: '1rem' }}>
                <SideMenu />
              </div>
            </Col>
          )}

          {/* メインコンテンツ: サイドバーの状態に応じて幅を調整 */}
          <Col
            xs={12}
            md={isSideMenuOpen ? 9 : 12}
            className="py-4 admin-main-content"
          >
            <div
              className="px-3"
              style={{ marginLeft: isSideMenuOpen ? '0' : '70px' }}
            >
              <h1 className="mb-4">ユーザー管理</h1>

              <Card className="shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Button
                      variant="primary"
                      onClick={handleManualRefresh}
                      disabled={loading}
                      className="d-flex align-items-center"
                    >
                      {loading && (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          className="me-2"
                        />
                      )}
                      <i className="fas fa-sync-alt me-2"></i>
                      {loading ? 'データ取得中...' : 'ユーザー一覧更新'}
                    </Button>
                    <div className="text-muted d-flex align-items-center">
                      {loading && users.length > 0 && (
                        <div className="me-3 d-flex align-items-center">
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            className="me-2"
                          />
                          <small>更新中...</small>
                        </div>
                      )}
                      {users.length > 0 && (
                        <>
                          <i className="fas fa-users me-2"></i>
                          <span>{users.length} 件のユーザー</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* 成功メッセージ */}
                  {success && (
                    <Alert
                      variant="success"
                      className="mb-3"
                      dismissible
                      onClose={() => setSuccess('')}
                    >
                      <Alert.Heading>
                        <i className="fas fa-check-circle me-2"></i>
                        操作が完了しました
                      </Alert.Heading>
                      {success}
                    </Alert>
                  )}

                  {/* エラーメッセージ */}
                  {error && (
                    <Alert
                      variant="danger"
                      className="mb-3"
                      dismissible
                      onClose={() => setError('')}
                    >
                      <Alert.Heading>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        エラーが発生しました
                      </Alert.Heading>
                      {error}
                    </Alert>
                  )}

                  {/* 初期ローディング時の表示 */}
                  {loading && users.length === 0 ? (
                    <div className="text-center py-5">
                      <Spinner
                        animation="border"
                        variant="primary"
                        className="mb-3"
                      />
                      <h5 className="text-muted">ユーザーデータを取得中...</h5>
                      <p className="text-muted">しばらくお待ちください。</p>
                    </div>
                  ) : (
                    <UserTable
                      users={users}
                      onUpdateUser={handleUpdateUser}
                      onDeleteUser={handleDeleteUser}
                    />
                  )}
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Users;
